import { defineStore } from 'pinia';
import { i18n } from 'src/boot/i18n';
import { LocalStorage } from 'quasar';
import helperUtil from 'src/utils/helperUtil';
import type { dynamicObj } from 'src/pages/assets';
import { replacer, reviver } from 'src/utils/mapUtil';
import { dashboardListViewService } from 'src/services';
import { getUserBasedKey } from 'src/utils/cacheHelper';
import { toBoolPreference } from 'src/utils/preferenceBool';
import displayFieldLabel from 'src/utils/displayFieldLabel';
import { isUndefinedOrNull, objLen } from 'src/utils/general';
import { useSettingStore, useUserStore } from 'src/store/main';
import { useColumnFilterStore } from 'src/store/column-filter.store';
import {
  FIELD_CATALOG,
  canonicaliseForBackend,
  parseSearchQuery,
  stripGroupByDirective,
  hasGroupByDirective,
} from 'src/pages/Dashboard/Filters/searchQueryParser';
import {
  filters_reset_after_export_setting,
  CONSTANTS,
  selected_dashboard_pref_key,
  dashboard_pagination,
  user_selected_dashboard_filters,
} from 'src/constants';

export const useDashboardStore = defineStore('dashboard_store', {
  state: () => ({
    pagination_size_key: 'PAGINATION_SIZE',
    pagination_size: 20,
    paginationCount: 1,
    processing: false,
    filters_key: CONSTANTS.pattern_dashboard_filters,
    columns_key: CONSTANTS.dashboard_columns_pref_key,
    duplicate_docs_columns_pref_key: CONSTANTS.duplicate_docs_columns_pref_key,
    sort_column_key: 'dashboard_sort',
    queryParams: '',
    show_analytics: false,
    showKeyboardShortcuts: false,
    all_invoices: 0,
    current_invoice_count: 0,
    automatically_processed: 0,
    manually_processed: 0,
    completed: 0,
    in_progress: 0,
    pending: 0,
    validated_pending_approval: 0,
    approved_pending_second_approval: 0,
    error: 0,
    upload_classify_doctype: '',
    order_by: {} as dynamicObj,
    visible_columns: {} as dynamicObj,
    filtered_columns: ['quality', 'po_matching'] as dynamicObj,
    display_data: [] as dynamicObj[],
    selected_dashboard: {} as any,
    // DOCB-12249 Bug #4: selectedGroupBy lives in dashboard.vue local state
    // but Custom Dashboard save/load happens in this store. Mirror it here
    // so getCustomization() can include it and setSelectedDashboard() can
    // restore it. dashboard.vue keeps its local state in sync via a watch.
    selectedGroupByState: null as { field: string; bucket?: string } | null,
    personal_dashboards: [] as dynamicObj[],
    filtered_personal_dashboards: [] as dynamicObj[],
    dashboard_updated: false,
    no_checks: false,
    notifyUpdate: false,
    // Global drag/dragging flags shared across dashboard and children
    isTabDragging: false,
    isColumnDragging: false,
    table_columns: {
      filename: {
        name: 'filename',
        translation_key: 'document_name',
        sortable: true,
        order: 0,
      },
      pages: {
        name: 'pages',
        translation_key: 'pages_dash',
        sortable: true,
        order: 2,
      },
      created_on: {
        name: 'created_on',
        translation_key: 'imported_on',
        sortable: true,
        order: 3,
      },
      status: {
        name: 'status',
        translation_key: 'status',
        sortable: true,
        order: 4,
      },
      priority: {
        name: 'priority',
        translation_key: 'priority',
        sortable: true,
        order: 5,
      },
      doc_type: {
        name: 'doc_type',
        translation_key: 'doc_type',
        sortable: true,
        order: 6,
      },
      assigned_to: {
        name: 'assigned_to',
        translation_key: 'assigned_to',
        sortable: true,
        order: 7,
      },
      last_saved_by: {
        name: 'last_saved_by',
        translation_key: 'last_saved_by',
        sortable: true,
        order: 8,
      },
      last_modified_on: {
        name: 'last_modified_on',
        translation_key: 'modified_at',
        sortable: true,
        order: 9,
      },
      erp_status: {
        name: 'erp_status',
        translation_key: 'erp_status',
        sortable: true,
        order: 10,
      },
      created_by: {
        name: 'created_by',
        translation_key: 'created_by',
        sortable: true,
        order: 11,
      },
      sub_org_name: {
        name: 'sub_org_name',
        translation_key: 'sub_org_id',
        sortable: true,
        order: 12,
      },
      invoice_due_date: {
        name: 'invoice_due_date',
        translation_key: 'invoice_due_date',
        doc_type_key: 'INVOICE',
        title: 'Invoice Due Date',
        sortable: true,
        order: 13,
      },
      discount_due_date: {
        name: 'discount_due_date',
        translation_key: 'discount_due_date',
        doc_type_key: 'INVOICE',
        title: 'Discount Due Date',
        sortable: true,
        order: 14,
      },
      discount_term: {
        name: 'discount_term',
        translation_key: 'discount_term',
        sortable: true,
        order: 15,
      },
      payment_term_description: {
        name: 'payment_term_description',
        translation_key: 'payment_term_description',
        sortable: true,
        order: 16,
      },
      ap_assignment_code: {
        name: 'ap_assignment_code',
        translation_key: 'ap_assignment_code',
        doc_type_key: 'INVOICE',
        title: 'AP Assignment Code',
        sortable: true,
        order: 17,
      },
    },
    show_loading_times: false,
    selected_doc_type_tab: '',
  }),

  getters: {
    quickFilterMap(): Map<string, any> {
      return useColumnFilterStore().quickFilterMap;
    },
    tempFilterMap(): Map<string, any> {
      return useColumnFilterStore().tempFilterMap;
    },
    // DRFS-583 — single source of truth for "can the user save changes
    // to the currently-selected dashboard?". Used by the save badge in
    // saved-filter-badges.vue and the "overwrite_current_view" entry in
    // multipleListView/List.vue. Both surfaces drifted out of sync —
    // the badge appeared when this returned true, but the list entry
    // remained disabled because it called a separate, slightly
    // different predicate. Anchor both to this getter.
    canSaveCurrentDashboard(): boolean {
      const dashboard = this.selected_dashboard;
      if (!dashboard || !dashboard.id) return false;
      if (dashboard.in_share) return false;
      return Boolean(this.dashboard_updated);
    },
  },

  actions: {
    setDraggingTab(flag: boolean) {
      // When a doc-type tab drag starts/ends; used by drag-drop mixins
      this.isTabDragging = flag;
    },
    setDraggingColumn(flag: boolean) {
      // When a column drag starts/ends; used by V2 table and drag-drop mixins
      this.isColumnDragging = flag;
    },
    checkForDashboardUpdates() {
      // ✅ Check if selected_dashboard exists before accessing properties
      if (!this.selected_dashboard || !this.selected_dashboard.id) {
        return;
      }
      if (this.selected_dashboard.in_share) {
        return;
      }
      if (this.no_checks) {
        return;
      }
      this.dashboard_updated = true;
    },
    saveFiltersToUserPreferences(filter_string: string) {
      if (!filter_string) {
        return;
      }
      const settings_store = useSettingStore();
      settings_store.setUserPreferences(
        user_selected_dashboard_filters,
        filter_string,
      );
    },

    saveSelectedDashboardToUserPreferences(dashboard_string: string) {
      const settings_store = useSettingStore();
      settings_store.setUserPreferences(
        selected_dashboard_pref_key,
        dashboard_string,
      );
    },

    async getSelectedDashboardFromUserPreferences() {
      const settings_store = useSettingStore();
      const response = await settings_store.getUserBasedSettings(
        selected_dashboard_pref_key,
      );
      if (!response) {
        return;
      }
      try {
        this.selected_dashboard = JSON.parse(response);
      } catch (err) {
        logError('[Dashboard] Failed to parse selected dashboard from preferences:', err);
      }
    },
    async getFiltersFromUserPreferences() {
      const settings_store = useSettingStore();
      const response = await settings_store.getUserBasedSettings(
        user_selected_dashboard_filters,
      );
      if (!response) {
        return;
      }
      return response;
    },
    async getPaginationSize() {
      const settings_store = useSettingStore();
      const pagination_size_setting = await settings_store.getUserBasedSettings(
        this.pagination_size_key,
      );
      if (
        isNaN(pagination_size_setting) ||
        typeof pagination_size_setting === 'object' ||
        !pagination_size_setting
      ) {
        this.pagination_size = 25;
      } else {
        this.pagination_size = pagination_size_setting;
      }
      return Number(this.pagination_size);
    },

    isDashboardSelected(): boolean {
      // ✅ Check if selected_dashboard exists before accessing .id
      return (
        this.selected_dashboard &&
        !isUndefinedOrNull(this.selected_dashboard.id)
      );
    },

    async getCustomization() {
      const columns = await this.handleTableColumns();
      const filters = JSON.stringify(this.quickFilterMap, replacer);
      const sorting = await this.getOrderBy();
      // Use current pagination_size from store state, not from user preferences
      const dashboard_page_count = Number(this.pagination_size) || 20;
      const customization = {
        columns: columns,
        filters: filters,
        sorting: sorting,
        dashboard_page_count: dashboard_page_count,
        // DOCB-12249 Bug #4: persist Group By selection so it survives
        // a Custom Dashboard reload. Was previously local-only on
        // dashboard.vue and silently lost on each page refresh.
        group_by: this.selectedGroupByState,
      };
      return JSON.stringify(customization);
    },

    buildFormDataFromJson(data: dynamicObj): any {
      const formData: any = new FormData();
      for (const [key, value] of Object.entries(data)) {
        if (!value) {
          continue;
        }
        if (key == 'org_id') {
          continue;
        }
        if (typeof value == 'string') {
          formData.append(key, value);
          continue;
        }
        formData.append(key, JSON.stringify(value));
      }
      return formData;
    },

    filterDashboardList(keyword: string) {
      if (!keyword) {
        this.filtered_personal_dashboards = this.personal_dashboards;
      }
      this.filtered_personal_dashboards = this.personal_dashboards.filter(
        (dashboard: any) => dashboard.name.includes(keyword),
      );
    },

    async deleteDashboardTemplate(id: string) {
      const formData = new FormData();
      formData.append('id', id);
      const response =
        await dashboardListViewService.deleteDashboardTemplate(formData);
      if (!response.success) {
        logError(response);
      }
      const removed_index = this.personal_dashboards.findIndex(
        (dashboard: any) => dashboard.id == id,
      );

      this.filtered_personal_dashboards.splice(removed_index, 1);
      this.personal_dashboards.splice(removed_index, 1);
    },
    async saveChangesToDashboard() {
      try {
        if (!this.selected_dashboard?.id) {
          throw { message: 'please_select_a_dashboard_to_continue' };
        }
        const customizations = await this.getCustomization()
        const data_obj = {
          id: this.selected_dashboard.id,
          name: this.selected_dashboard.name,
          shared: this.selected_dashboard.shared,
          customizations: customizations,
        };
        const edit_data = this.buildFormDataFromJson(data_obj);
        await this.editDashboardTemplate(edit_data);
        this.dashboard_updated = false;
        await this.getDashboardListView();
      } catch (err) {
        logError(err);
      }
    },

    async resetViewToDefault() {
      this.resetFilters();
      this.setDefaultFilters();
      const settings_store = useSettingStore();
      settings_store.setUserPreferences(selected_dashboard_pref_key, '');
      this.selected_dashboard = {};
      // DOCB-12249 Bug #4: clear Group By on full reset alongside other state.
      this.selectedGroupByState = null;

      // Load user's saved pagination size preference
      await this.getPaginationSize();

      const event = new CustomEvent(this.columns_key + '_updated', {});
      document.dispatchEvent(event);

      const tabs_event = new CustomEvent('apply_tabs_filter', { detail: {} });
      document.dispatchEvent(tabs_event);

      // DRFS-583 — wipe the chip editor in lockstep with the filter
      // reset. resetFilters() empties quickFilterMap, but the editor
      // owns its own document state and would otherwise keep rendering
      // the previous query as chips.
      document.dispatchEvent(
        new CustomEvent('load_saved_filter', { detail: { raw: '' } }),
      );

      // Reload data with the correct pagination size
      const apply_filters_event = new CustomEvent('apply_filters', { detail: {} });
      document.dispatchEvent(apply_filters_event);
    },

    isUserInShared(shared: string, created_by: number): boolean {
      if (!shared) {
        return false;
      }

      const ids = shared.split(',');
      const user_store = useUserStore();
      const user_id = user_store.getUserDetailByKey('id').toString();

      let found = false;

      if (user_id == created_by.toString()) {
        return found;
      }
       
      ids.forEach((id: string) => {
        if (id == user_id) {
          found = true;
        }

        const groups = user_store.userDetail.groups;
        if (!groups) {
          return;
        }

        groups.forEach((group: any) => {
          if (id == group.id) {
            found = true;
          }
        });
      });
      return found;
    },

    isDashboardActive(id: string) {
      // ✅ Check if selected_dashboard exists before accessing .id
      return this.selected_dashboard && this.selected_dashboard.id == id;
    },

    async getDashboardListView() {
      const response = await dashboardListViewService.getDashboardTemplates();
      const user_store = useUserStore();
      const user_id = user_store.getUserDetailByKey('id').toString();
      await user_store.getUserDetailWithGroupAndSubOrgByUserId(user_id);
      for (let i = response.data.length - 1; i >= 0; i--) {
        const dashboard = response.data[i];
        if (this.isUserInShared(dashboard.shared, dashboard.created_by)) {
          dashboard.in_share = true;
        }
        dashboard.customizations = this.populateColumnDataFromServer(
          dashboard.customizations,
        );
      }
      this.personal_dashboards = JSON.parse(JSON.stringify(response.data));

      this.filtered_personal_dashboards = response.data;
    },
    
    async selectDashboard(dashboard: any, no_checks = false, overwrite = false, skipColumnEvent = false) {
      try {
        this.processing = true;
        if (no_checks) {
          this.no_checks = no_checks;
          this.dashboard_updated = false;
        }
        this.selected_dashboard = dashboard;
        const dashboard_string = JSON.stringify(dashboard);
        const settings_store = useSettingStore();
        settings_store.setUserPreferences(selected_dashboard_pref_key, dashboard_string);

        // DOCB-12249 Bug #4: restore Group By from the saved customization.
        // Defensive `?? null` for dashboards saved before this field
        // was persisted — they simply load with no group-by selected.
        this.selectedGroupByState = dashboard.customizations?.group_by ?? null;

        const columns = JSON.parse(JSON.stringify(dashboard.customizations.columns));
        // skipColumnEvent=true skips dispatching columns_key_updated event to avoid duplicate dashboard requests
        this.saveSortedColumns(columns, CONSTANTS.dashboard_columns_pref_key, skipColumnEvent); // Apply Columns

        // Set pagination_size from dashboard, fallback to user preference if not set
        if (dashboard.customizations?.dashboard_page_count) {
          this.pagination_size = dashboard.customizations.dashboard_page_count;
        } else {
          // Dashboard doesn't have page count saved, load from user preferences
          await this.getPaginationSize();
        }
        this.checkForDashboardUpdates();

        this.order_by = dashboard.customizations.sorting;
        this.saveOrderBy();

        const filter_str = dashboard.customizations?.filters;
        let filters = {};
        if (filter_str) {
          try {
            filters = JSON.parse(filter_str, reviver);
          } catch (err) {
            logError('[Dashboard] Failed to parse filters:', err);
          }
        }

        if (overwrite) {
          this.overWriteFilters(filters); // Apply Filters to Dashboard
          // DRFS-583 — sync the chip editor with the destination
          // dashboard's saved raw_query (or empty when the dashboard
          // has none). Without this, the editor visually retains the
          // previous query even though quickFilterMap was already
          // swapped, leaving stale chips on screen.
          let nextRawQuery =
            filters instanceof Map ? (filters.get('raw_query') ?? '') : '';
          // DOCB-12249 (Codex review MEDIUM #2): a dashboard saved
          // BEFORE the round-trip-chip fix carries
          // `customizations.group_by` (restored above into
          // `selectedGroupByState`) but its persisted raw_query was
          // stripped of the directive. Without this synthesis the
          // chip editor would rehydrate without the `Gruppieren
          // nach :` chip while the table is grouped. Only append when
          // the saved raw_query doesn't already carry a directive —
          // post-fix dashboards persist the full form already.
          const gb = this.selectedGroupByState;
          if (gb && gb.field && !hasGroupByDirective(nextRawQuery)) {
            const directive = `group by ${gb.field}${gb.bucket ? ' ' + gb.bucket : ''}`;
            nextRawQuery = nextRawQuery.trim()
              ? `${nextRawQuery.trim()} ${directive}`
              : directive;
          }
          document.dispatchEvent(
            new CustomEvent('load_saved_filter', {
              detail: { raw: nextRawQuery },
            }),
          );
        }

        // Dispatch apply_filters to reload data with correct pagination
        // Skip during initial mount (skipColumnEvent=true) to avoid duplicate data loads
        if (!skipColumnEvent) {
          const event = new CustomEvent('apply_filters', { detail: {} });
          document.dispatchEvent(event);
        }

        const tabs_event = new CustomEvent('apply_tabs_filter', {
          detail: { filters },
        });
        document.dispatchEvent(tabs_event);
      } catch (err) {
        logError(err);
      } finally {
        this.processing = false;
        setTimeout(() => {
          this.no_checks = false;
        }, 1000);
      }
    },
    
    async editDashboardTemplate(formData: FormData) {
      
      const response = await dashboardListViewService.editDashboardTemplate(formData);
      if (!response.success) {
        logError(response);
        throw response;
      }
      const id = formData.get('id');
      const updated_index = this.personal_dashboards.findIndex(
        (dashboard: any) => dashboard.id == id,
      );

      if (response.data) {
        this.personal_dashboards[updated_index] = response.data;
        this.filtered_personal_dashboards[updated_index] = response.data;
      }
      
      if (formData.get('id') == this.selected_dashboard.id) {
        await this.selectDashboard(JSON.parse(JSON.stringify(response.data)))
      }
    },

    async createDashboardTemplate(data: dynamicObj) {
      const response: any =
        await dashboardListViewService.createDashboardTemplate(data);
      if (!response.success) {
        logError(response);
      } else {
        this.personal_dashboards.push(response.data);
        this.filtered_personal_dashboards.push(response.data);
      }
       
      return response;
    },

    async setPaginationSize() {
      const settings_store = useSettingStore();
      const valueToSave = this.pagination_size <= 50 ? this.pagination_size : '50';
      await settings_store.setUserPreferences(
        this.pagination_size_key,
        valueToSave,
      );
    },
    getPaginationCount() {
      const sessionObject = LocalStorage.getItem(
        getUserBasedKey(dashboard_pagination),
      ) as dynamicObj;
      //if pagination size is same as previous then return the pagination count
      if (
        sessionObject &&
        sessionObject.pagination_size == this.pagination_size
      ) {
        this.paginationCount = sessionObject.paginationCount;
      }
      return sessionObject ? parseInt(sessionObject.paginationCount) : 1;
    },
    saveSortedColumns(
      tableColumns: any,
      columns_key = CONSTANTS.dashboard_columns_pref_key,
      isFromSort = false,
    ) {
      const visibleColumnsSorted = JSON.parse(JSON.stringify(tableColumns));
      const visibleColumnsSortedObject = {} as any;
      let columnsArray = tableColumns;
      if (this.selected_dashboard && this.selected_dashboard.id) {
        /*const columns = this.populateColumnDataFromServer(visibleColumnsSorted);*/
        columnsArray = Array.isArray(visibleColumnsSorted)
          ? visibleColumnsSorted
          : Object.values(visibleColumnsSorted);
      }
      columnsArray.forEach((column: any, index: number) => {
        if (!column || Object.keys(column).length === 0) return;
        visibleColumnsSortedObject[column.name] = {
          name: column.name,
          label: column.label,
          order: index,
          sortable: column.sortable,
          selected: false,
        };
        const defaultCol = this.table_columns[column.name];
        const effectiveDocTypeKey = column.doc_type_key || defaultCol?.doc_type_key;
        if (effectiveDocTypeKey) {
          visibleColumnsSortedObject[column.name].title = column.title || defaultCol?.title;
          visibleColumnsSortedObject[column.name].doc_type_key = effectiveDocTypeKey;
        }
      });
      this.visible_columns[columns_key] = visibleColumnsSortedObject;
      this.saveColumns(
        this.visible_columns[columns_key],
        columns_key,
        isFromSort,
      );
    },
    async saveColumns(
      visible_columns: dynamicObj,
      columns_key = CONSTANTS.dashboard_columns_pref_key,
      isFromSort = false,
    ) {
      this.checkForDashboardUpdates();
      if ( !this.selected_dashboard || Object.keys(this.selected_dashboard).length === 0 ) {
        const str = JSON.stringify(visible_columns);
        this.visible_columns[columns_key] = visible_columns;

        const settings_store = useSettingStore();
        await settings_store.updateSetting(columns_key.toUpperCase(), str);
      } else {
        // Ensure customizations object exists before setting columns
        if (!this.selected_dashboard.customizations) {
          this.selected_dashboard.customizations = {};
        }
        this.selected_dashboard.customizations.columns = visible_columns;
      }

      if (isFromSort) {
        return;
      }
      const event = new CustomEvent(columns_key + '_updated', {});
      document.dispatchEvent(event);
    },

    async getSavedColumns(columns_key = CONSTANTS.dashboard_columns_pref_key) {
      let columns = [] as dynamicObj;
      try {
        if (this.selected_dashboard && this.selected_dashboard.id) {
          columns = this.selected_dashboard.customizations.columns;
          // Convert array to object with column names as keys for addAdditionalDocTypeFields
          if (Array.isArray(columns)) {
            const columnsObject = {} as any;
            columns.forEach((column: any) => {
              if (column && column.name) {
                columnsObject[column.name] = column;
              }
            });
            columns = columnsObject;
          }

          this.visible_columns[columns_key] = columns;
        } else {
          // check from server
          const saved_columns = await this.getDashboardColumnFromServer(columns_key);
          if (!saved_columns) {
            this.visible_columns[columns_key] = this.table_columns;
          } else {
            this.visible_columns[columns_key] = JSON.parse(saved_columns);
          }
          columns = this.visible_columns[columns_key];
        }
      } catch (err) {
        this.visible_columns[columns_key] = this.table_columns;
      }

      // Normalize array to object before backfill (saved preferences may be arrays)
      if (Array.isArray(columns)) {
        const columnsObject = {} as any;
        columns.forEach((column: any) => {
          if (column && column.name) {
            columnsObject[column.name] = column;
          }
        });
        columns = columnsObject;
        this.visible_columns[columns_key] = columns;
      }

      // Backfill doc_type_key from table_columns defaults for columns
      // that were saved before doc_type_key was added (DRFS-534 fix)
      if (columns && typeof columns === 'object') {
        for (const key in columns) {
          const col = columns[key];
          if (col && !col.doc_type_key) {
            const defaultCol = this.table_columns[col.name || key];
            if (defaultCol?.doc_type_key) {
              col.doc_type_key = defaultCol.doc_type_key;
              col.title = col.title || defaultCol.title;
            }
          }
        }
      }

      return columns;
    },

    async getDashboardColumnFromServer(
      columns_key = CONSTANTS.dashboard_columns_pref_key,
    ) {
      try {
        const settings_store = useSettingStore();
        const columns_setting =
          await settings_store.user_settings[columns_key.toUpperCase()];
        return columns_setting as string;
      } catch (err) {
        return '';
      }
    },

    async getOrderBy() {
      try {
        if (objLen(this.order_by) !== 0) {
          return this.order_by;
        }

        this.order_by = {} as dynamicObj;
        const settings_store = useSettingStore();
        const order_by_setting = await settings_store.getUserBasedSettings(
          this.sort_column_key.toUpperCase(),
        );
        this.order_by = JSON.parse(order_by_setting);
      } catch (err) {}

      return this.order_by;
    },

    async saveOrderBy() {
      this.checkForDashboardUpdates();
      try {
        const str = JSON.stringify(this.order_by);

        const settings_store = useSettingStore();
        await settings_store.setUserPreferences(
          this.sort_column_key.toUpperCase(),
          str,
        );

        const event = new CustomEvent('dashboard_order_by_updated', {});
        document.dispatchEvent(event);
      } catch (err) {}
    },

    getSavedFilters() {
      useColumnFilterStore().getSavedFilters(this.filters_key);
    },

    setDefaultFilters() {
      const columnFilterStore = useColumnFilterStore();
      // Skip the default `filter: last_90_days` window when the user
      // already has a date constraint — either via the date-range
      // picker (`date_range` / `filter`) or via a structured
      // date-field predicate in `filter_predicates` / `raw_query`
      // (e.g. imported_on, invoice_due_date). Without this check the
      // default gets re-added on every makeQueryParamFromFilters() call
      // and overrides the user's explicit window.
      const hasDateFieldPredicate = (() => {
        const DATE_FIELDS = new Set([
          'imported_on',
          'created_on',
          'last_modified_on',
          'updated',
          'updated_on',
          'invoice_date',
          'invoice_due_date',
          'discount_due_date',
          'terms_net_due_date',
          'terms_discount_date',
        ]);
        const isDateField = (field: string) => {
          const meta = FIELD_CATALOG.find((f) => f.name === field);
          return meta?.valueType === 'date' || DATE_FIELDS.has(field);
        };

        const rawPredicates = columnFilterStore.quickFilterMap.get(
          'filter_predicates',
        );
        try {
          if (rawPredicates && typeof rawPredicates === 'string') {
            const arr = JSON.parse(rawPredicates) as Array<{ field: string }>;
            if (arr.some((p) => isDateField(p.field))) return true;
          }
        } catch {
          // Ignore malformed legacy JSON and fall back to raw_query parsing.
        }

        const rawQuery = columnFilterStore.quickFilterMap.get('raw_query');
        if (!rawQuery || typeof rawQuery !== 'string') return false;
        try {
          return parseSearchQuery(rawQuery).predicates.some((p) =>
            isDateField(p.field),
          );
        } catch {
          return false;
        }
      })();

      if (
        !columnFilterStore.quickFilterMap.has('filter') &&
        !columnFilterStore.quickFilterMap.has('date_range') &&
        !hasDateFieldPredicate
      ) {
        columnFilterStore.quickFilterMap.set('filter', {
          label: 'last_90_days',
          value: 'last_90_days',
        });
      }
    },

    overWriteFilters(filterMap: any) {
      useColumnFilterStore().overWriteFilters(filterMap, this.filters_key);
    },

    // dashboard filters handling after export
    async dashboardFiltersAfterExport() {
      const settings_store = useSettingStore();
      const filter_setting =
        await settings_store.user_settings[filters_reset_after_export_setting];
      if (filter_setting) {
        this.resetFilters();
      }
    },

    resetFilters() {
      useColumnFilterStore().resetFilters(this.filters_key);
    },

    saveFilters() {
      useColumnFilterStore().saveFilters(this.filters_key);
    },

    removeSavedFilters() {
      getUserBasedKey(this.filters_key);
    },

    async makeQueryParamFromFilters(save = true) {
      this.queryParams = '';
      const settings_store = useSettingStore();
      const isFulltext = settings_store.org_based_settings['OPENSEARCH_ENABLED']

      this.setDefaultFilters();
      for (const [key, item] of this.quickFilterMap.entries()) {
        if (!key || !item || key === 'tab_status') {
          continue;
        }

        let filter_value = item;
        let filter_name = key;

        if (isFulltext) {
          if (filter_name === 'filter' || filter_name === 'date_range') {
            continue;
          }
        }

        if (
          typeof item === 'object' &&
          item !== null &&
          Object.prototype.hasOwnProperty.call(item, 'value')
        ) {
          filter_value = item.value;
        }

        if (typeof filter_value === 'object' && filter_value !== null) {
          continue;
        }

        if (filter_value === 'all_invoices') {
          continue;
        }

        if (key === 'order_by' || key === 'status_list' || key === 'user_id_list' || key == 'group_id_list') {
          continue;
        } 

        if (key === 'date_range' && !isFulltext) {
          const dateArray = filter_value.split(CONSTANTS.dateSplitBy);

          if (dateArray[0] != CONSTANTS.questionSymbol) {
            this.queryParams += '&start_date' + '=' + dateArray[0];
          }

          if (dateArray[1] != CONSTANTS.questionSymbol) {
            this.queryParams += '&end_date' + '=' + dateArray[1];
          }
          continue;
        }

        if (key === 'restart_filter') {
          filter_name = 'restart_allowed';
        }

        // Range predicates are JSON-encoded — must be URL-encoded so the
        // backend Form-parser gets the literal string intact. Otherwise
        // the '=' inside the JSON would be interpreted as a new param
        // separator and the payload would arrive truncated. `raw_query`
        // carries the same hazard: predicate `=`, spaces, parens, and
        // `AND`/`OR` glue would slice the value at `&` boundaries
        // without encoding.
        if (key === 'filter_predicates' || key === 'raw_query') {
          // raw_query is persisted in the user's typed form (so URLs
          // and chip round-trips stay readable); canonicalise aliases
          // (imported_on → created_on) and relative dates (today() →
          // ISO) only at the HTTP boundary. Other values pass through.
          //
          // DOCB-12249 (Hanna's comment #86606 follow-up): the trailing
          // `group by <field>` directive lives in the persisted
          // raw_query so the QuickSearch chip editor can rehydrate the
          // `Gruppieren nach :` chip on remount. The backend, however,
          // takes the group axis through the separate `group_by` slot
          // and would 422 on a trailing `group by …` token — strip it
          // here before canonicalisation.
          const serialised =
            key === 'raw_query'
              ? canonicaliseForBackend(
                  stripGroupByDirective(filter_value as string),
                )
              : filter_value;
          this.queryParams +=
            '&' + filter_name + '=' + encodeURIComponent(serialised);
          continue;
        }

        this.queryParams += '&' + filter_name + '=' + filter_value;
      }

      // only for mobile
      if (
        !this.queryParams.includes('validated_pending_approval') &&
        helperUtil.methods.isMobile()
      ) {
        this.queryParams = '&status=validated_pending_approval';
      }

      if (objLen(this.order_by) !== 0) {
        this.queryParams += '&order_by_field=' + this.order_by.field_name;
        this.queryParams += '&order_by_direction=' + this.order_by.direction;
      }

      const user_store = useUserStore();
      const org_id = user_store.getOrgId();
      const sub_org_id = await user_store.getSubOrgId();

      if (sub_org_id && sub_org_id != org_id) {
        this.queryParams += '&sub_org_id=' + sub_org_id;
        this.queryParams += '&show_only_from_selected_sub_org=true';
      }

      if (org_id) {
        this.queryParams += '&org_id=' + org_id;
      }

      if (save) {
        this.saveFilters();
      }

      this.queryParams += this.addAdditionalDocTypeFields();
      return this.queryParams;
    },

    addAdditionalDocTypeFields(
      columns_key = CONSTANTS.dashboard_columns_pref_key,
    ) {
      const doc_type_fields = [];

      // Fix: Use same logic as handleTableColumns() to get the correct columns
      // When a saved/shared dashboard is selected, use its customizations.columns
      // Otherwise fall back to visible_columns
      let columnsToCheck = {} as dynamicObj;
      if (this.selected_dashboard && this.selected_dashboard.id) {
        columnsToCheck = this.selected_dashboard.customizations.columns;
      } else {
        columnsToCheck = this.visible_columns[columns_key];
      }

      for (const key in columnsToCheck) {
        if (!columnsToCheck[key].hasOwnProperty('doc_type_key')) {
          continue;
        }

        // Use column.name if available (for numeric keys), otherwise use key
        const fieldName = columnsToCheck[key].name || key;
        doc_type_fields.push(fieldName);
      }

      if (doc_type_fields.length === 0) {
        return '';
      }

      // Reset sort if order_by is a document_field — the backend pivot-table
      // ORDER BY breaks when combined with the standard (non-pivot) query path.
      // OpenSearch (fulltext) indexes document_fields and can sort them, so
      // the rewrite only applies to the Postgres path.
      const settings_store = useSettingStore();
      const isFulltext = toBoolPreference(settings_store.org_based_settings['OPENSEARCH_ENABLED']);
      if (!isFulltext && this.order_by?.field_name && doc_type_fields.includes(this.order_by.field_name)) {
        this.queryParams = this.queryParams
          .replace(`&order_by_field=${this.order_by.field_name}`, '&order_by_field=last_modified_on')
          .replace(`&order_by_direction=${this.order_by.direction}`, '&order_by_direction=desc');
      }

      return '&document_fields=' + doc_type_fields.toString();
    },

    getColumnLabel(item: dynamicObj) {

      if (!item.translation_key) {
        const table_column = this.table_columns[item.name as keyof typeof this.table_columns];
        item.translation_key = table_column?.translation_key || item.name;
      }

      item.label = i18n.t(item.translation_key);

      if (item.hasOwnProperty('doc_type_key')) {
        item.label = displayFieldLabel.methods.getFieldLabelFromAllPrefixes(item);
      }

      return String(item.label);
    },
    async handleTableColumns(
      update_loading_flag = true,
      columns_key = CONSTANTS.dashboard_columns_pref_key,
      fetchFromServer = false,
    ) {
      const table_columns = [] as dynamicObj[];
      try {
        this.processing = true;
        const settings_store = useSettingStore();
        const isFulltext = toBoolPreference(settings_store.org_based_settings['OPENSEARCH_ENABLED']);
        let visible_columns = [] as dynamicObj;
        if (this.selected_dashboard && this.selected_dashboard.id) {
          visible_columns = this.selected_dashboard.customizations.columns;
        } else if (fetchFromServer) {
          const columnsStr = await this.getDashboardColumnFromServer(columns_key);
          visible_columns = columnsStr ? JSON.parse(columnsStr) : {};
          this.visible_columns[columns_key] = visible_columns;
        } else {
          visible_columns = this.visible_columns[columns_key];
        }
        for (const key in visible_columns) {
          visible_columns[key].selected = false;
          const isCustomField = Object.prototype.hasOwnProperty.call(
            visible_columns[key],
            'doc_type_key',
          );
          visible_columns[key].sortable = isCustomField ? isFulltext : true;
          if (!this.filtered_columns.includes(visible_columns[key].name)) {
            visible_columns[key].label = this.getColumnLabel(
              visible_columns[key],
            );
            table_columns.push(visible_columns[key]);
          }
        }
        // sort
        table_columns.sort((a: any, b: any) => {
          return a.order - b.order;
        });
      } catch (err) {
        logError(err);
      } finally {
        if (update_loading_flag) {
          this.processing = false;
        }
      }

      return table_columns;
    },
    /**
     * Fetches columns from server and processes them
     * Wrapper for handleTableColumns with fetchFromServer=true
     */
    async fetchAndHandleTableColumns(
      update_loading_flag = true,
      columns_key = CONSTANTS.dashboard_columns_pref_key,
    ) {
      return this.handleTableColumns(update_loading_flag, columns_key, true);
    },
    getParentRows(parentsIndexArray: any): any {
      const parentRows = [] as any;
      let previousRow = {} as any;

      for (const parentIndex of parentsIndexArray) {
        if (Object.entries(previousRow).length > 0) {
          previousRow = previousRow._children[Number(parentIndex)];
        } else {
          previousRow = this.display_data[Number(parentIndex)];
        }

        parentRows.push(previousRow);
      }

      return parentRows;
    },

    stripColumnDataForSave(dashboardColumns: dynamicObj) {
      /*const strippedColumns: Record<string, { name: string; doc_type_key: string | null }> = {};
      for (const key in dashboardColumns) {
        const { name, doc_type_key = null } = dashboardColumns[key] || {};
        strippedColumns[name] = { name, doc_type_key };
      }*/
      return dashboardColumns;
    },
    populateColumnDataFromServer(dashboardColumns: dynamicObj) {
      /*const finalColumns: Record<string, any> = {};

      for (const key in dashboardColumns.columns) {
        const customCol = dashboardColumns.columns[key];
        const baseCol = Object.values(this.visible_columns[this.columns_key]).find(c => (c as dynamicObj).name === customCol.name);
        if (baseCol) {
          // Merge base + custom, ignoring null/undefined
          const merged: Record<string, any> = { ...baseCol };
          for (const prop in customCol) {
            const value = customCol[prop];
            if (value !== null && value !== undefined) {
              merged[prop] = value;
            }
          }
          finalColumns[customCol.name] = merged;
        } else {
          // New column → copy + defaults
          finalColumns[customCol.name] = {
            ...customCol,
            selected: false,
            sortable: true,
          };
        }
      }
      //dashboardColumns.columns = finalColumns;*/
      return dashboardColumns;
    },
  },
});
